import { type Location, type Strings, type WebConfig } from '@koala/sdk';
import Link from 'next/link';
import { Fragment } from 'react';
import { useTheme } from 'styled-components';
import {
  CardInfoRow,
  getLocationAvailability,
  LocationConveyance,
} from '@/components/locations/card/components';
import { CrossStreet } from '@/components/locations/crossStreet';
import StringAccessor from '@/components/cmsConfig/stringAccessor';
import { ClockIcon } from '@/components/uielements/icon/clock';
import { MapIcon } from '@/components/uielements/icon/map';
import { CTA } from '@/constants/cta';
import { LOCATION_ATTRIBUTE_POSITIONS } from '@/constants/locations';
import { PhoneIcon } from '@/features/menu-availability/icons/phone';
import { type IBasketFulfillment } from '@/types/fulfillment';
import { fireGaEvent, gaActions, gaCats } from '@/utils/googleAnalytics';
import { deriveTodaysHours, googleMapsUrl } from '@/utils/locations';
import {
  interpolateLocationCtaValue,
  reconcileCustomAttributeWithGlobalValue,
  safelyGetString,
} from '@/utils/stringHelpers';
import { phoneNumberFormatter } from '@/utils/auth';

const deriveCTAValues = (
  strings: Strings,
  location: Location,
  position: string,
  positionId: number,
  basketFulfillment: IBasketFulfillment,
  config: WebConfig,
) => {
  const globalValue = safelyGetString(strings, `location_ctas.${position}_value`, location);
  const globalLabel = safelyGetString(strings, `location_ctas.${position}_label`, location);
  const { label, value } = reconcileCustomAttributeWithGlobalValue(
    // @ts-expect-error @TODO improve typing of custom attributes
    location.attributes,
    positionId,
    globalValue,
    globalLabel,
  );

  const ctaObject: CTAObject = {
    value,
    label,
    interpolatedValue: interpolateLocationCtaValue(value, location, config).finalValue,
  };

  // If we have an existing delivery address AND the CTA is a delivery trigger, treat the CTA
  // like the pickup button and just navigate to store menu page - the reconciliation modal will handle the rest
  if (basketFulfillment?.address && value === CTA.DELIVERY_URL) {
    ctaObject.interpolatedValue = interpolateLocationCtaValue(
      CTA.PICKUP_URL,
      location,
      config,
    ).finalValue;
  }

  return ctaObject;
};

interface CTAObject {
  label: string;
  value: string;
  interpolatedValue: string;
}

const derivePrimaryCtas = (
  ctaObject: CTAObject,
  location: Location,
  gaAction: string,
  classes: string,
  hideDeliveryTrigger: boolean,
  basketFulfillment: IBasketFulfillment,
  toggleFulfillmentModal: () => void,
) => {
  const escapedLocationLabel = location.label?.replace(/'/g, '');

  if (ctaObject.value === CTA.DELIVERY_URL && !basketFulfillment?.address) {
    return (
      <button
        className={classes}
        data-css-override="MapTooltipPrimaryButton"
        aria-label={`${ctaObject.label} from ${location.label}`}
        onClick={() => {
          toggleFulfillmentModal();
          fireGaEvent(gaCats.order, gaAction, {
            label: escapedLocationLabel,
          });
        }}
        style={{ display: hideDeliveryTrigger ? 'none' : 'block' }}
      >
        {ctaObject.label}
      </button>
    );
  }

  if (ctaObject.label && ctaObject.value && location.provider_enabled) {
    return (
      <Link
        href={ctaObject.interpolatedValue}
        className={classes}
        onClick={() =>
          fireGaEvent(gaCats.order, gaAction, {
            label: escapedLocationLabel,
          })
        }
        data-css-override="MapTooltipPrimaryButton"
      >
        {ctaObject.label}
      </Link>
    );
  }

  return null;
};

const ORDER_CTAS = [
  {
    position: 'primary',
    positionId: LOCATION_ATTRIBUTE_POSITIONS.PRIMARY_ORDER_CTA,
    gaAction: gaActions.mapPrimaryCta,
    themeKey: 'primary_button_theme',
  },
  {
    position: 'secondary',
    positionId: LOCATION_ATTRIBUTE_POSITIONS.SECONDARY_ORDER_CTA,
    gaAction: gaActions.mapSecondaryCta,
    themeKey: 'secondary_button_theme',
  },
] as const;

interface MapTooltipProps {
  location: Location;
  toggleFulfillmentModal: () => void;
  strings: Strings;
  basketFulfillment: IBasketFulfillment;
}

/**
 * The map pin's popup (TEAM-15027): name, address, phone, availability, fulfillment, then the
 * order CTAs last. Availability and fulfillment come from the same helpers as the location card
 * (TEAM-15026) so the popup and the card never disagree. The `tooltip-*` class names are kept
 * because brand CSS targets them.
 */
export const MapTooltip = ({
  location,
  toggleFulfillmentModal,
  strings,
  basketFulfillment,
}: MapTooltipProps) => {
  const theme = useTheme();
  const { cached_data } = location;

  // We should only render the Koala-specific delivery trigger if the location supports delivery. Otherwise, we should render nothing
  const hideDeliveryTrigger = !location.supports_delivery;

  // Location inactive: no order CTAs.
  const disabled = !location.provider_enabled;

  const phone = phoneNumberFormatter(cached_data?.phone_number);
  // An inactive location always says why. The card hides this copy when a custom CTA attribute
  // replaces its order buttons, but the popup never renders that button, so it would say nothing.
  const availability = disabled ? (
    <StringAccessor accessor="locations.location_inactive" html={true} />
  ) : (
    getLocationAvailability(
      location,
      deriveTodaysHours(location, cached_data ? cached_data.utc_offset : 0),
    )
  );

  return (
    <div className="tooltip" data-testid="map-tooltip">
      <div className="tooltip-info">
        <h3>{cached_data?.label}</h3>
        <CardInfoRow icon={<MapIcon />} testId="map-tooltip-address-row">
          <a href={googleMapsUrl(cached_data)} target="_blank" rel="noreferrer">
            {location.street_address}
            <br />
            <CrossStreet crossStreetDetail={location.cross_street || null} />
            {location.city}, {cached_data?.state ?? ''} {location.zip_code ?? ''}
          </a>
        </CardInfoRow>
        {phone && (
          <CardInfoRow icon={<PhoneIcon />} testId="map-tooltip-phone-row">
            <span>{phone}</span>
          </CardInfoRow>
        )}
        <div className="tooltip-status">
          {availability && (
            <CardInfoRow icon={<ClockIcon />} testId="map-tooltip-availability-row">
              {availability}
            </CardInfoRow>
          )}
          <LocationConveyance location={location} />
        </div>
      </div>
      <div className="tooltip-ctas" data-testid="map-tooltip-ctas">
        {!disabled &&
          ORDER_CTAS.map(({ position, positionId, gaAction, themeKey }) => (
            <Fragment key={position}>
              {derivePrimaryCtas(
                deriveCTAValues(strings, location, position, positionId, basketFulfillment, theme),
                location,
                gaAction,
                `tooltip-cta tooltip-cta-${theme.location_card?.[themeKey]}`,
                hideDeliveryTrigger,
                basketFulfillment,
                toggleFulfillmentModal,
              )}
            </Fragment>
          ))}
      </div>
    </div>
  );
};
